import React, { useState, useEffect } from "react";
import { ColumnDef } from "@tanstack/react-table";
import styles from "./medicalActivityGroups.module.css";
import { Toaster } from "../../components/ui/toaster";
import DataTable from "../../shared/DataTable";
import { Button } from "../../components/ui/button";
import { isViewer } from "../../interface/RoleHelpers";
import { MedicalActivityAxiosInstance } from "../../Network/medicalActivity";
import { Datum, ActivityCode, TYPE_CLASS } from "../../interface/MedicalActivity";
import FileUpload from "../../UploadFile/MedicalActivity";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { CODE_TO_ACTIVITY_TYPE } from "../../component/ActivityTypeSelector";
import { AlertTriangle } from "lucide-react";

const MedicalActivity: React.FC = () => {
  // =====================================
  // Main Data State
  // =====================================
  const [allItems, setAllItems] = useState<Datum[]>([]);

  // =====================================
  // Pagination State
  // =====================================
  const [searchInput, setSearchInput] = useState(""); 
  const [search, setSearch] = useState(""); 
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // =====================================
  // Selected / Editing State
  // =====================================
  const [selectedActivity, setSelectedActivity] = useState<ActivityCode[]>();
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [loading, setLoading] = useState(true);

  // =====================================
  // Dialog & Modal Refs
  // =====================================
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [ActivityDialogOpen, setActivityDialogOpen] = useState(false);
  const [warningDialog, setWarningDialog] = useState<{
    open: boolean;
    item: Datum | null;
    usageData: any;
    loadingUsage: boolean;
  }>({ open: false, item: null, usageData: null, loadingUsage: false });
  const [showAllLogs, setShowAllLogs] = useState(false);
  const { toast } = useToast();

  const handleOpenDialog = async (item: Datum) => {
    try {
      const response = await MedicalActivityAxiosInstance.get(`medical-group/activity/${item.id}`);
      const fullData = response.data.data || response.data;
      setSelectedItem(fullData);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch group details:", error);
      toast({
        title: "Error",
        description: "Failed to load group details. Please try again.",
        variant: "destructive",
      });
    }
  };
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedItem(null);
    setShowAllLogs(false);
  };

  const [advancedSearchInput, setAdvancedSearchInput] = useState({
    groupName: "",
    activityCode: "",
    activityType: "",
    status: "",
  });
  const [advancedSearch, setAdvancedSearch] = useState({
    groupName: "",
    activityCode: "",
    activityType: "",
    status: "",
  });

  const clearFilters = () => {
    setAdvancedSearchInput({ groupName: "", activityCode: "", activityType: "", status: "" });
    setSearchInput("");
  };

  // =====================================
  // Error Handling
  // =====================================
  const getApiErrorMessage = (error: any): string => {
    if (!error) return "Unknown error";
    if (error.response?.data?.error?.message)
      return error.response.data.error.message;
    if (error.response?.data?.message) return error.response.data.message;
    if (error.message) return error.message;
    return "An unexpected error occurred.";
  };
  
  // =====================================
  // Debounce
  // =====================================
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => {
      const cleanedCode =
        advancedSearchInput.activityCode.trim().length >= 3
          ? advancedSearchInput.activityCode.trim()
          : "";

      setAdvancedSearch({
        groupName: advancedSearchInput.groupName.trim(),
        activityCode: cleanedCode,
        activityType: advancedSearchInput.activityType, 
        status: advancedSearchInput.status,
      });
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [advancedSearchInput]);

  const buildFilterParams = (): Record<string, any> => {
    const params: Record<string, any> = {
      includeCodes: true,
    };
    if (search) params.name = search;
    if (advancedSearch.groupName) params.name = advancedSearch.groupName;
    if (advancedSearch.activityCode) params.code = advancedSearch.activityCode;
    if (advancedSearch.status) params.active = advancedSearch.status === "Active";
    return params;
  };

  const hasActiveFilters = Boolean(
    search ||
      advancedSearch.groupName ||
      advancedSearch.activityCode ||
      advancedSearch.activityType ||
      advancedSearch.status
  );

  // =====================================
  // Fetch Functions (Server-side pagination + search + filters)
  // =====================================
  useEffect(() => {
    fetchItems();
  }, [page, limit, search, advancedSearch]);

  const fetchItems = async () => {
    try {
      setLoading(true);

      const params: Record<string, any> = {
        page,
        limit,
        ...buildFilterParams(),
      };

      const res = await MedicalActivityAxiosInstance.get("medical-group/activity", {
        params,
      });

      const responseData = res.data.data;
      const items = responseData?.items ?? [];

      setAllItems(items);
      setTotalPages(responseData?.totalPages ?? 1);
      setTotalItems(responseData?.total ?? items.length);

      const pages = responseData?.totalPages ?? 1;
      if (page > pages) {
       setPage(Math.max(1, pages));
      }
    } catch (error) {
      console.error(error);
       toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const displayedItems = advancedSearch.activityType
    ? allItems.filter((item) =>
        item.codes?.some(
          (c) =>
            (CODE_TO_ACTIVITY_TYPE[c.type?.toString()] || "").toLowerCase() ===
            advancedSearch.activityType.toLowerCase(),
        ),
      )
    : allItems;

  // =====================================
  // toggleActive Function
  // =====================================
  const toggleActive = async (row: Datum) => {
    if (row.isActive) {
      try {
        const res = await MedicalActivityAxiosInstance.get(`medical-group/activity/usage/${row.id}`);
        const usageData = res.data.data;

        if (!usageData.isUsed || usageData.totalUsageCount === 0) {
          confirmToggle(row);
        } else {
          setWarningDialog({ open: true, item: row, usageData: usageData, loadingUsage: false });
        }
      } catch (error) {
        console.error("Error fetching usage:", error);
      }
    } else {
      confirmToggle(row);
    }
  };

  const confirmToggle = async (row: Datum) => {
    try {
      await MedicalActivityAxiosInstance.patch(
        `medical-group/activity/${row.id}/activation`,
        { isActive: !row.isActive },
      );
      toast({
        variant: "success",
        title: "Success",
        description: `Status updated to ${!row.isActive ? "active" : "inactive"}`,
      });
      setAllItems((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, isActive: !item.isActive } : item,
        ),
      );
    } catch (error: any) {
       toast({
        title: "Error",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const formatText = (text: string) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // =====================================
  // Activity Type Color
  // =====================================
  const getActivityTypeColor = (type: string) => {
    switch (type) {
      case "CPT":
        return { bg: "bg-blue-100", border: "border-blue-200", text: "text-blue-800" };
      case "HCPCS":
        return { bg: "bg-green-100", border: "border-green-200", text: "text-green-800" };
      case "Dental":
        return { bg: "bg-purple-100", border: "border-purple-200", text: "text-purple-800" };
      case "Service":
        return { bg: "bg-orange-100", border: "border-orange-200", text: "text-orange-800" };
      default:
        return { bg: "bg-gray-100", border: "border-gray-200", text: "text-gray-800" };
    }
  };

  // =====================================
  // Download Function
  // =====================================
  const handleDownload = async (item: Datum) => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await MedicalActivityAxiosInstance.get(`medical-group/activity/${item.id}/export`, {
        responseType: 'blob',
      });
      
      const disposition: string = response.headers?.['content-disposition'] ?? '';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
      const filename = match ? decodeURIComponent(match[1]) : `MedicalActivity_${item.name || "export"}.xlsx`;

      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        variant: "success",
        title: "Export Finished",
        description: `"${item.name}" exported successfully.`,
      });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download export file", variant: "destructive" });
    }
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query || query.trim().length < 3) return text;
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    if (!lowerText.includes(lowerQuery)) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === lowerQuery ? (
        <span key={index} className="bg-yellow-50 text-yellow-900 px-1 rounded font-semibold">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // =====================================
  // Table Columns
  // =====================================
  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="medicalActivity-page-column-name-sort-btn"
        >
          Name
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <div className="text-center">{row.getValue("name")}</div>,
    },
    {
      accessorKey: "codes",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-black w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="medicalActivity-page-column-codes-sort-btn"
        >
          Activity Codes
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const activities = row.getValue("codes") as ActivityCode[];

        if (!activities || activities.length === 0) {
          return <span className="text-gray-400 text-center block">-</span>;
        }

        const searchTerm =
          advancedSearch.activityCode.trim().length >= 3
            ? advancedSearch.activityCode.toLowerCase().trim()
            : search.trim().length >= 3
              ? search.toLowerCase().trim()
              : "";
        const hasSearch = searchTerm.length >= 3;

        const displayActivities = activities.slice(0, 2);
        const remainingCount = activities.length - 2;

        return (
          <div className="flex flex-col items-center gap-1">
            <div className="flex flex-wrap justify-center items-center gap-2">
              {displayActivities.map((activity, idx) => {
                const isMatch = hasSearch && activity.code.toLowerCase().includes(searchTerm);
                const typeLabel = CODE_TO_ACTIVITY_TYPE[String(activity.type)] ?? activity.type;
                const colors = getActivityTypeColor(typeLabel);

                return (
                  <div
                    key={idx}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${
                      isMatch ? "bg-yellow-50 border-yellow-400" : `${colors.bg}${colors.border}`
                    }`}
                    data-testid={`medicalActivity-page-row-${row.original.id}-code-chip-${idx}`}
                  >
                    <span className={`text-xs font-semibold ${isMatch ? "text-yellow-900" : colors.text}`}>
                      {highlightMatch(activity.code, searchTerm)}
                    </span>
                    <span className={`text-[10px] font-bold ${isMatch ? "text-yellow-700" : colors.text}`}>
                      {typeLabel}
                    </span>
                  </div>
                );
              })}

              {remainingCount > 0 && (
                <button
                  onClick={() => { setSelectedActivity(activities); setActivityDialogOpen(true); }}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  data-testid={`medicalActivity-page-row-${row.original.id}-codes-more-btn`}
                >
                  +{remainingCount} more
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: () => (
        <div className="w-full font-bold fa-1x text-black text-center normal-case">Status</div>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => toggleActive(item)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                item.isActive ? "bg-green-500" : "bg-gray-300"
              }`}
              data-testid={`medicalActivity-page-row-${item.id}-status-toggle`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  item.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="w-full font-bold text-black text-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          data-testid="medicalActivity-page-column-view-sort-btn"
        >
          View
          {column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="w-full flex justify-center py-2">
            <button
              onClick={() => handleOpenDialog(item)}
              className="text-blue-600 hover:text-blue-800"
              title="View Details"
              data-testid={`medicalActivity-page-row-${item.id}-view-btn`}
            >
              <i className="fa-solid fa-eye text-lg"></i>
            </button>
          </div>
        );
      },
    },
  ];

  // =====================================
  // Codes Section — Legend helper
  // =====================================
  const buildLegend = (codes: ActivityCode[]) => {
    const counts: Record<string, number> = {};
    codes.forEach((c) => {
      const t = CODE_TO_ACTIVITY_TYPE[c.type?.toString()] || "CPT";
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  };

  return (
    <div data-testid="medicalActivity-page">
      <div className={styles.container} data-testid="medicalActivity-page-container">
        <h5 className={styles.title} data-testid="medicalActivity-page-title">
          <span className={styles.dialogIcon} data-testid="medicalActivity-page-title-icon">
            <i className="fa-solid fa-file-medical"></i>
          </span>
          Medical Activity Grouping
        </h5>
        <FileUpload
          totalItems={totalItems}
          hasFilters={hasActiveFilters}
          exportParams={buildFilterParams()}
        />
      </div>

      {/* Advanced Search Section */}
      <div
        className={`${styles["compact-search-container"]} ${styles["mb-6"]}`}
        data-testid="medicalActivity-page-search-container"
      >
        <div className={styles["search-wrapper"]}>
          <div className={styles["search-header-compact"]}>
            <div className={styles["header-info"]}>
              <i className="fa-solid fa-filter"></i>
              <span>Search Filters</span>
            </div>
            <button
              onClick={clearFilters}
              className={styles["btn-clear-compact"]}
              data-testid="medicalActivity-page-search-reset-btn"
            >
              <i className="fa-solid fa-rotate-left"></i>
              Reset
            </button>
          </div>

          <div
            className={styles["search-fields-compact"]}
            style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem" }}
          >
            {/* 1. Group Name */}
            <div className={styles["field-compact"]} data-testid="medicalActivity-page-search-groupname">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-layer-group"></i> Group Name
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Search groups..."
                  value={advancedSearchInput.groupName}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, groupName: e.target.value })}
                  className={styles["input-compact"]}
                  data-testid="medicalActivity-page-search-groupname-input"
                />
                {advancedSearchInput.groupName && (
                  <button
                    onClick={() => setAdvancedSearchInput({ ...advancedSearchInput, groupName: "" })}
                    className={styles["clear-btn-compact"]}
                    data-testid="medicalActivity-page-search-groupname-clear-btn"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* 2. Activity Code */}
            <div className={styles["field-compact"]} data-testid="medicalActivity-page-search-activitycode">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-code"></i> Activity Code
              </label>
              <div className={styles["input-container-compact"]}>
                <input
                  type="text"
                  placeholder="Min 3 characters..."
                  value={advancedSearchInput.activityCode}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, activityCode: e.target.value })}
                  className={`${styles["input-compact"]} ${
                    advancedSearchInput.activityCode.trim().length > 0 &&
                    advancedSearchInput.activityCode.trim().length < 3
                      ? styles["input-warning-compact"]
                      : ""
                  }`}
                  data-testid="medicalActivity-page-search-activitycode-input"
                />
                {advancedSearchInput.activityCode && (
                  <button
                    onClick={() => setAdvancedSearchInput({ ...advancedSearchInput, activityCode: "" })}
                    className={styles["clear-btn-compact"]}
                    data-testid="medicalActivity-page-search-activitycode-clear-btn"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>
              {advancedSearchInput.activityCode.trim().length > 0 &&
                advancedSearchInput.activityCode.trim().length < 3 && (
                  <span className="hint-compact" data-testid="medicalActivity-page-search-activitycode-hint">
                    <i className="fa-solid fa-exclamation-circle"></i> Min 3 chars
                  </span>
                )}
            </div>

            <div className={styles["field-compact"]} data-testid="medicalActivity-page-search-activitytype">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-tag"></i> Activity Type
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  value={advancedSearchInput.activityType}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, activityType: e.target.value })}
                  className={styles["select-compact"]}
                  data-testid="medicalActivity-page-search-activitytype-select"
                >
                  <option value="">All Types</option>
                  <option value="CPT">CPT</option>
                  <option value="HCPCS">HCPCS</option>
                  <option value="Dental">Dental</option>
                  <option value="Service">Service</option>
                </select>
              </div>
            </div>

            {/* 4. Status */}
            <div className={styles["field-compact"]} data-testid="medicalActivity-page-search-status">
              <label className={styles["label-compact"]}>
                <i className="fa-solid fa-toggle-on"></i> Status
              </label>
              <div className={styles["select-container-compact"]}>
                <select
                  value={advancedSearchInput.status}
                  onChange={(e) => setAdvancedSearchInput({ ...advancedSearchInput, status: e.target.value })}
                  className={styles["select-compact"]}
                  data-testid="medicalActivity-page-search-status-select"
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className={styles["stats-compact"]}>
            <i className="fa-solid fa-chart-bar"></i>
            <span className={styles["stats-text-compact"]}>
              Showing <strong>{displayedItems.length}</strong> of{" "}
              <strong>{totalItems}</strong> groups
            </span>
            {(advancedSearch.groupName ||
              advancedSearch.activityCode.length >= 3 ||
              advancedSearch.activityType ||
              advancedSearch.status) && (
              <span className={styles["badge-filtered-compact"]}>
                <i className="fa-solid fa-filter"></i> Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* View Dialog */}
      {isDialogOpen && selectedItem && (
        <dialog open className={styles["modern-dialog"]} data-testid="medicalActivity-page-dialog">
          <div className={styles["dialog-container"]} data-testid="medicalActivity-page-dialog-container">
            <div className={styles["dialog-header"]} data-testid="medicalActivity-page-dialog-header">
              <div className={styles["header-content"]}>
                <div className={styles["header-icon"]} data-testid="medicalActivity-page-dialog-header-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"
                      stroke="currentColor" strokeWidth="2"
                    />
                    <path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div>
                  <h2 className={styles["dialog-title"]} data-testid="medicalActivity-page-dialog-title">
                    Group Details
                  </h2>
                  <p className={styles["dialog-subtitle"]} data-testid="medicalActivity-page-dialog-subtitle">
                    {selectedItem.name}
                  </p>
                </div>
              </div>
              <button className={styles["close-btn"]} onClick={handleCloseDialog} data-testid="medicalActivity-page-dialog-close-btn">
                ✕
              </button>
            </div>

            <div className={styles["dialog-body"]} data-testid="medicalActivity-page-dialog-body">
              <section className={styles.section} data-testid="medicalActivity-page-dialog-basic-info">
                <h3 className={styles["section-title"]}>Basic Information</h3>
                <div className={styles["info-group"]}>
                  <div className={styles["info-row"]} data-testid="medicalActivity-page-dialog-row-id">
                    <span className={styles["row-label"]}>Group ID</span>
                    <span className={styles["info-text"]}>
                      {selectedItem
                        ? (page - 1) * limit + displayedItems.findIndex((i) => i.id === selectedItem.id) + 1
                        : ""}
                    </span>
                  </div>
                  <div className={styles["info-row"]} data-testid="medicalActivity-page-dialog-row-name">
                    <span className={styles["row-label"]}>Group Name</span>
                    <span className={styles["info-text"]}>{selectedItem.name}</span>
                  </div>
                  <div className={styles["info-row"]} data-testid="medicalActivity-page-dialog-row-activitytype">
                    <span className={styles["row-label"]}>Activity Type</span>
                    <span className={styles["info-text"]}>
                      {selectedItem?.codes && selectedItem.codes.length > 0
                        ? CODE_TO_ACTIVITY_TYPE[selectedItem.codes[0].type] || "Unknown"
                        : "Unknown"}
                    </span>
                  </div>
                  <div className={styles["info-row"]} data-testid="medicalActivity-page-dialog-row-status">
                    <span className={styles["row-label"]}>Status</span>
                    <div
                      className={`${styles["status-badge-inline"]} ${selectedItem.isActive ? styles.active : styles.inactive}`}
                      data-testid="medicalActivity-page-dialog-status-badge"
                    >
                      <span className={styles["status-dot"]}></span>
                      {selectedItem.isActive ? "Active" : "Inactive"}
                    </div>
                  </div>
                </div>
              </section>

              <section
                className={`${styles.section} ${styles["full-width"]}`}
                data-testid="medicalActivity-page-dialog-codes-section"
              >
                <div className={styles["codes-section-wrapper"]}>
                  <div className={styles["codes-header"]}>
                    <div className={styles["codes-header-left"]}>
                      <div className={styles["codes-icon-box"]}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M4 6h16M4 10h16M4 14h10M4 18h7" stroke="#4fa3e0" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div>
                        <div className={styles["codes-header-text-title"]}>Codes</div>
                        <div className={styles["codes-header-text-sub"]}>Procedure codes list</div>
                      </div>
                    </div>

                    <div
                      className={styles["codes-count-badge"]}
                      data-testid="medicalActivity-page-dialog-codes-count"
                    >
                      <div className={styles["codes-count-dot"]} />
                      <span className={styles["codes-count-number"]}>
                        {selectedItem.codes?.length || 0}
                      </span>
                    </div>
                  </div>

                  <div className={styles["codes-divider"]} />

                  <div
                    className={styles["codes-scroll-area"]}
                    data-testid="medicalActivity-page-dialog-codes-scroll"
                  >
                    {selectedItem.codes?.length ? (
                      <div
                        className={styles["codes-grid"]}
                        data-testid="medicalActivity-page-dialog-codes-grid"
                      >
                        {selectedItem.codes.map((code, idx) => {
                          const displayType = code.type
                            ? CODE_TO_ACTIVITY_TYPE[code.type.toString()] || code.type
                            : "CPT";
                          const cls = TYPE_CLASS[displayType] || "cpt";

                          return (
                            <div
                              key={idx}
                              className={`${styles["code-card"]} ${styles[`type-${cls}`]}`}
                              data-testid={`medicalActivity-page-dialog-code-${idx}`}
                            >
                              <div className={`${styles["code-card-bar"]} ${styles[cls]}`} />
                              <div style={{ display: "flex", alignItems: "baseline", gap: "4px", paddingLeft: "6px" }}>
                                <span className={styles["code-card-hash"]}>#</span>
                                <span className={styles["code-card-number"]}>{code.code}</span>
                              </div>
                              <span className={`${styles["code-type-badge"]} ${styles[cls]}`}>
                                {displayType}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div
                        className={styles["empty-state-mini"]}
                        data-testid="medicalActivity-page-dialog-codes-empty"
                      >
                        No Activity Assigned
                      </div>
                    )}
                  </div>

                  {selectedItem.codes?.length ? (
                    <div className={styles["codes-footer"]} data-testid="medicalActivity-page-dialog-codes-legend">
                      {Object.entries(buildLegend(selectedItem.codes)).map(([key, count]) => {
                        const cls = TYPE_CLASS[key] || "cpt";
                        return (
                          <div key={key} className={styles["codes-legend-item"]} data-testid={`medicalActivity-page-dialog-codes-legend-${key}`}>
                            <div className={`${styles["codes-legend-dot"]} ${styles[cls]}`} />
                            <span className={styles["codes-legend-label"]}>
                              {key}{" "}
                              <span className={styles[`codes-legend-count-${cls}`]}>{count}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </section>

              {/* Audit Trail */}
              <div style={{ padding: "20px 28px 8px" }} data-testid="medicalActivity-page-dialog-audit-trail">
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                </div>

                {(selectedItem as any).auditLog?.length > 0 && (
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px" }} data-testid="medicalActivity-page-dialog-audit-history">
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" }}>Update History</span>
                      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
                        {(selectedItem as any).auditLog.length} update{(selectedItem as any).auditLog.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(selectedItem as any).auditLog
                        .slice(0, showAllLogs ? undefined : 4)
                        .map((log: any, i: number) => (
                          <div key={log.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }} data-testid={`medicalActivity-page-dialog-audit-log-${log.id}`}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff" }}>
                                {String(log.actorName ?? "?")[0].toUpperCase()}
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{log.actorName}</span>
                              {i === 0 && (
                                <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>
                                  Latest
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
                              {new Date(log.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={styles["dialog-footer"]} data-testid="medicalActivity-page-dialog-footer">
              <button className={styles["btn-secondary"]} onClick={handleCloseDialog} data-testid="medicalActivity-page-dialog-footer-close-btn">
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}

      {/* All Activity Codes Dialog */}
      <Dialog open={ActivityDialogOpen} onOpenChange={setActivityDialogOpen} data-testid="medicalActivity-page-Activity-dialog">
        <DialogContent className="max-w-lg modern-dialog bg-white Activity-dialog" data-testid="medicalActivity-page-Activity-dialog-content">
          <DialogHeader className="dialog-header">
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2 dialog-title" data-testid="medicalActivity-page-Activity-dialog-title">
              <i className="fa-solid fa-list-check"></i>
              All Activity Codes
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 dialog-body" data-testid="medicalActivity-page-Activity-dialog-body">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 section">
              <div
                className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2 Activity-scroll-area"
                style={{ scrollbarWidth: "thin", scrollbarColor: "#3b82f6 #dbeafe" }}
                data-testid="medicalActivity-page-Activity-dialog-codes-grid"
              >
                {selectedActivity && selectedActivity.map((activity, index) => {
                  const typeName = CODE_TO_ACTIVITY_TYPE[activity.type.toString()] || "Unknown";
                  return (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all Activity-card-mini"
                      data-testid={`medicalActivity-page-Activity-dialog-code-${index}`}
                    >
                      <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0 mini-icon">
                        <i className="fa-solid fa-building text-sm"></i>
                      </div>
                      <span className="text-gray-800 font-medium flex-1 Activity-name">
                        {`${activity.code} (${typeName})`}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-blue-200" data-testid="medicalActivity-page-Activity-dialog-total">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <i className="fa-solid fa-info-circle text-blue-600"></i>
                  <span>Total: <strong className="text-blue-900">{selectedActivity?.length || 0}</strong> Activity Codes</span>
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="dialog-footer">
            <Button onClick={() => setActivityDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700 btn-secondary" data-testid="medicalActivity-page-Activity-dialog-close-btn">
              <i className="fa-solid fa-check mr-2"></i>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog */}
      <Dialog
        data-testid="medicalActivity-page-warning-dialog"
        open={warningDialog.open}
        onOpenChange={(open) =>
          !open && setWarningDialog({ open: false, item: null, usageData: null, loadingUsage: false })
        }
      >
        <DialogContent className="max-w-md bg-white p-0 overflow-hidden rounded-2xl flex flex-col max-h-[90vh]" data-testid="medicalActivity-page-warning-dialog-content">
          <div className="p-6 pb-2 flex-shrink-0">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600 font-bold">
                <AlertTriangle className="h-5 w-5" />
                Warning : Group in use
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-sm">
                Changing Status to <strong>inactive</strong> may affect predefined rules.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 flex-1 min-h-0 flex flex-col">
            {warningDialog.usageData?.isUsed && (
              <div className={styles.alertContainer}>
                <div className="rounded-lg bg-amber-50 p-3 border border-amber-100 flex-shrink-0">
                  <p className="text-xs text-amber-800 font-medium text-center">
                    Currently active in <strong>{warningDialog.usageData.totalUsageCount}</strong> location(s).
                  </p>
                </div>

                <div className={styles.alertScrollArea}>
                  {warningDialog.usageData.icdValidationRules?.length > 0 && (
                    <div className={styles.alertSection}>
                      <h4 className={styles.alertSectionTitle}>ICD Rules</h4>
                      {warningDialog.usageData.icdValidationRules.map((rule: any) => (
                        <div key={rule.ruleId} className={styles.alertCard}>
                          <span className="text-sm font-medium">{rule.ruleName}</span>
                          <span className={styles.alertBadge}>{formatText(rule.ruleType)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {warningDialog.usageData.activityValidationRules?.length > 0 && (
                    <div className={styles.alertSection}>
                      <h4 className={styles.alertSectionTitle}>Activity Rules</h4>
                      {warningDialog.usageData.activityValidationRules.map((rule: any) => (
                        <div key={rule.ruleId} className={styles.alertActivityCard}>
                          <span className="text-sm font-medium">{rule.ruleName}</span>
                          <span className={styles.alertActivityBadge}>{formatText(rule.ruleType)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {warningDialog.usageData.secondaryActivityValidationRules?.length > 0 && (
                    <div className={styles.alertSection}>
                      <h4 className={styles.alertSectionTitle}>Activity Two</h4>
                      {warningDialog.usageData.secondaryActivityValidationRules.map((rule: any) => (
                        <div key={rule.ruleId} className={styles.alertActivityCard}>
                          <span className="text-sm font-medium">{rule.ruleName}</span>
                          <span className={styles.alertActivityBadge}>{formatText(rule.ruleType)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {warningDialog.usageData.customActivityGroups?.length > 0 && (
                    <div className={styles.alertSection}>
                      <h4 className={styles.alertSectionTitle}>Custom Groups</h4>
                      {warningDialog.usageData.customActivityGroups.map((group: any) => (
                        <div key={group.groupId} className={styles.alertCustomGroupContainer}>
                          Group: <strong>{group.groupName}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <p className={styles.alertFooterQuestion}>
                  Are you sure you want to proceed?
                </p>
              </div>
            )}
          </div>

          <div className="p-6 pt-4 flex-shrink-0 bg-white">
            <DialogFooter className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-10 text-sm"
                onClick={() => setWarningDialog((prev) => ({ ...prev, open: false }))}
                data-testid="medicalActivity-page-warning-dialog-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-xl h-10 bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm"
                onClick={() => {
                  confirmToggle(warningDialog.item!);
                  setWarningDialog((prev) => ({ ...prev, open: false }))
                }}
                data-testid="medicalActivity-page-warning-dialog-proceed-btn"
              >
                Yes, Proceed
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster data-testid="medicalActivity-page-toaster" />

      <div className={styles.tableWrapper} data-testid="medicalActivity-page-table-wrapper">
        <DataTable
          data={displayedItems}
          columns={columns}
          onDownload={handleDownload}
          addButtonLabel=""
          pageIndex={page - 1}
          pageSize={limit}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(size) => { setLimit(size); setPage(1); }}
          isLoading={loading}
          globalSearch={searchInput}
          onGlobalSearchChange={setSearchInput}
          data-testid="medicalActivity-page-datatable"
        />
      </div>
    </div>
  );
};

export default MedicalActivity;